import * as z from "zod";
import { readAskRedoConfirmationData } from "../../confirmation-data";

/**
 * `action` stays an open string so new mutation actions render without a
 * client change; it only picks the approve-button label.
 */
const returnMutationConfirmationSchema = z.object({
  kind: z.literal("return-mutation-confirmation"),
  action: z.string().optional(),
  orderNumber: z.string().optional(),
  summary: z.string(),
  warnings: z.array(z.string()),
});

/** A destructive return mutation staged for merchant approval. */
export type ReturnMutationConfirmation = z.infer<
  typeof returnMutationConfirmationSchema
>;

/**
 * Reads a return-mutation confirmation out of a tool part's confirmation
 * data; undefined for every other approval, so callers fall back to generic copy.
 */
export function readReturnMutationConfirmation(
  input: unknown,
): ReturnMutationConfirmation | undefined {
  const confirmation = readAskRedoConfirmationData(input);
  if (!confirmation) {
    return undefined;
  }
  const parsed = returnMutationConfirmationSchema.safeParse(
    confirmation.confirmationData,
  );
  return parsed.success ? parsed.data : undefined;
}

const APPROVE_LABEL_BY_ACTION: Record<string, string> = {
  approve: "Approve return",
  close: "Close return",
  process: "Process return",
  reopen: "Reopen return",
  reset: "Reset return",
};

/** Approve-button label for the staged action. */
export function returnMutationApproveLabel(
  confirmation: ReturnMutationConfirmation,
): string {
  return (
    (confirmation.action && APPROVE_LABEL_BY_ACTION[confirmation.action]) ||
    "Confirm"
  );
}
